/**
 * SegmentLabelManager — Shows distance labels on each polygon edge.
 *
 * [MAP v5] SaaS Production-Grade upgrade 2026-07-14
 * [MAP v8] BUG-1 fix 2026-07-15: deep-flatten LatLng arrays so L.geoJSON wrappers
 *   (which return [[LatLng, ...]] or [[[LatLng, ...]]] for MultiPolygon) never
 *   produce index numbers as label text.
 * [MAP v8] BUG-4 fix 2026-07-15: zoom-aware label skip — hide labels for edges
 *   whose screen length is below a minimum pixel threshold to prevent overlap.
 * [MAP v40] M1 fix 2026-07-16: flattenLatLngs `break` bug — caused only 1 point
 *   to be returned for L.Polygon.getLatLngs() = [[LatLng,...]] format, making
 *   labels never render on saved polygons. Fixed by detecting ring vs wrapper arrays.
 * [Sprint v73 STEP H] 2026-07-18: per-layer labelsByLayer Map + null-placeholder
 *   invariant + updateForLayerDrag (2-label DOM mutation, no full re-render) +
 *   exported extractOuterRing + computeAreaPerimeter for attachLiveMeasure.
 *
 * Key design decisions (from Claude deep-dive):
 * - Attach events to LAYER (not map) for pm:markerdrag
 * - Use L.divIcon with interactive:false to prevent click interference
 * - iconAnchor: [30, 10] for centering (not margin-left: -50%)
 * - Cleanup all markers in destroy()
 * - R11: labelsByLayer Map<leaflet_id, (marker|null)[]> for multi-polygon support
 * - R12: _renderLabels pushes null for EVERY skipped edge (index invariant)
 * - R8: updateForLayerDrag mutates textContent + setLatLng (no DOM destroy during drag)
 */

import { formatPerimeter } from "./areaUnitConversion";
import { resolveDrawLabelPlacement, type ScreenPoint } from "./drawLabelPlacement";
import { shouldRefreshDrawLabels, shouldUpdateDrawLabelPosition } from "./drawLabelStability";

/**
 * A label below this distance is visual noise caused by an incomplete or
 * duplicate transient draw point. Geometry is never filtered by this helper.
 */
export const MIN_DISPLAYABLE_SEGMENT_METERS = 0.5;

export function isDisplayableSegmentDistance(distanceMeters: unknown): distanceMeters is number {
  return typeof distanceMeters === "number"
    && Number.isFinite(distanceMeters)
    && distanceMeters >= MIN_DISPLAYABLE_SEGMENT_METERS;
}

interface LatLng {
  lat: number;
  lng: number;
  distanceTo: (other: LatLng) => number;
}

/**
 * Check if a value is a Leaflet LatLng object.
 */
function isLatLng(v: any): v is LatLng {
  return (
    typeof v === "object" &&
    v !== null &&
    !Array.isArray(v) &&
    typeof v.lat === "number" &&
    typeof v.lng === "number"
  );
}

/**
 * Deep-flatten any nested LatLng array structure into a flat LatLng[].
 * Handles: LatLng[], [LatLng[]], [[LatLng[]]] (MultiPolygon), etc.
 * A LatLng object is identified by having numeric .lat and .lng properties.
 *
 * [MAP v40] M1 FIX: Previous version used `break` after first sub-result,
 * which caused L.Polygon.getLatLngs() = [[LatLng,...]] to return only 1 point:
 *   flattenLatLngs([[LatLng x5]])
 *     → item = [LatLng x5]
 *     → flattenLatLngs([LatLng x5])
 *         → item = LatLng → base case → [LatLng]
 *         → BREAK → returns [LatLng] (only 1!)
 *     → pushes [LatLng] → BREAK → result = [LatLng]
 * Fix: detect if array IS the ring (contains LatLng objects directly) and
 * return all items without breaking early.
 */
function flattenLatLngs(input: any): LatLng[] {
  if (!input) return [];

  // Base case: it's a LatLng object
  if (isLatLng(input)) {
    return [input];
  }

  // Recursive case: it's an array
  if (Array.isArray(input)) {
    // M1 FIX: Check if this array IS the ring (contains LatLng objects directly).
    // L.Polygon.getLatLngs() returns [[LatLng,...]] — the outer array wraps the ring.
    // When we reach [LatLng,...], we must collect ALL items, not just the first.
    const isRing = input.length > 0 && isLatLng(input[0]);
    if (isRing) {
      // This IS the outer ring — return all valid LatLng objects
      return input.filter(isLatLng) as LatLng[];
    }

    // Otherwise it's a nested wrapper (e.g. [[LatLng,...]] or [[[LatLng,...]]]) —
    // iterate items and return the first ring with >= 3 points (outer ring).
    // We do NOT break early here; we look for the first valid ring.
    for (const item of input) {
      const sub = flattenLatLngs(item);
      if (sub.length >= 3) return sub; // Found the outer ring
    }
    return [];
  }

  return [];
}

/**
 * Extract the outer ring LatLngs from any Leaflet layer.
 * Handles L.Polygon, L.Rectangle, L.GeoJSON wrapper, and nested rings.
 * Exported for use in CollateralMapModal.tsx attachLiveMeasure.
 */
export function extractOuterRing(layer: any): LatLng[] {
  // Case 1: Direct polygon layer with getLatLngs()
  if (typeof layer.getLatLngs === "function") {
    const raw = layer.getLatLngs();
    return flattenLatLngs(raw);
  }

  // Case 2: L.GeoJSON wrapper — iterate sub-layers
  if (typeof layer.getLayers === "function") {
    const subLayers = layer.getLayers();
    for (const sub of subLayers) {
      if (typeof sub.getLatLngs === "function") {
        const raw = sub.getLatLngs();
        const flat = flattenLatLngs(raw);
        if (flat.length >= 3) return flat;
      }
    }
  }

  return [];
}

/**
 * Compute geodesic area (m2) and perimeter (m) for a flat LatLng array.
 * Uses Shoelace formula (same as calcAreaFromLatLngs in CollateralMapModal).
 * Exported for use in CollateralMapModal.tsx attachLiveMeasure.
 */
export function computeAreaPerimeter(latLngs: LatLng[]): { areaM2: number; perimeter: number } {
  if (latLngs.length < 3) return { areaM2: 0, perimeter: 0 };

  // Area via Shoelace / spherical excess approximation
  const R = 6371000;
  let area = 0;
  const n = latLngs.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const lat1 = (latLngs[i].lat * Math.PI) / 180;
    const lat2 = (latLngs[j].lat * Math.PI) / 180;
    const dLng = ((latLngs[j].lng - latLngs[i].lng) * Math.PI) / 180;
    area += dLng * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  const areaM2 = Math.abs((area * R * R) / 2);

  // Perimeter via distanceTo
  let perimeter = 0;
  for (let i = 0; i < n; i++) {
    const a = latLngs[i];
    const b = latLngs[(i + 1) % n];
    if (typeof a.distanceTo === "function") {
      perimeter += a.distanceTo(b);
    }
  }

  return { areaM2, perimeter };
}

/**
 * Minimum pixel distance between two screen points below which we skip the label.
 * Prevents label overlap at low zoom levels.
 */
const MIN_SCREEN_DIST_PX = 8; // v15: reduced from 30 — large polygons at low zoom have edges 10-30px, must still show labels

export class SegmentLabelManager {
  private map: any;
  // R11: per-layer Map keyed by leaflet_id — supports multi-polygon without cross-contamination
  // R12: values are (marker|null)[] — null placeholder for every skipped edge (index invariant)
  private labelsByLayer: Map<number, (any | null)[]> = new Map();
  private L: any;
  private isDrawingHandleDragging = false;

  constructor(L: any, map: any) {
    this.L = L;
    this.map = map;
  }

  private _layerId(layer: any): number {
    return layer?._leaflet_id ?? 0;
  }

  /**
   * Clear ALL labels across all layers.
   */
  clearLabels(): void {
    this.isDrawingHandleDragging = false;
    this.labelsByLayer.forEach((markers) => {
      markers.forEach((m) => {
        if (m) try { m.remove(); } catch {}
      });
    });
    this.labelsByLayer.clear();
  }

  /**
   * Freeze presentation-only drawing labels while a custom midpoint or vertex
   * handle follows the pointer. Geometry and the live segment overlay continue
   * to update; the final committed drag state refreshes labels exactly once.
   */
  setDrawingHandleDragging(active: boolean): void {
    this.isDrawingHandleDragging = active;
  }

  /**
   * Clear labels for a specific layer only (used by updateForLayerDrag fallback).
   */
  clearLabelsForLayer(layer: any): void {
    const id = this._layerId(layer);
    const markers = this.labelsByLayer.get(id) ?? [];
    markers.forEach((m) => {
      if (m) try { m.remove(); } catch {}
    });
    this.labelsByLayer.set(id, []);
  }

  /**
   * Update labels for a given layer (polygon/rectangle/L.geoJSON wrapper).
   * Clears only THIS layer's labels then re-renders.
   * Call after pm:create, pm:edit, zoomend, moveend.
   */
  updateForLayer(layer: any): void {
    this.clearLabelsForLayer(layer);
    const latLngs = extractOuterRing(layer);
    if (latLngs.length < 2) return;
    this._renderLabels(this._layerId(layer), latLngs, false, true);
  }

  /**
   * Update labels for a working layer during drawing.
   * Reuses existing Leaflet markers during live midpoint drags. Removing and
   * recreating div markers on every frame causes mobile labels to blink.
   */
  updateForDrawingVertices(vertices: LatLng[]): void {
    // BUG-v284: never reposition or mutate label DOM during active handle drag.
    // Repeated collision placement plus marker transforms was the remaining
    // Android flicker source after v283 marker reuse.
    if (!shouldRefreshDrawLabels(this.isDrawingHandleDragging)) return;
    const DRAW_ID = -1;
    const markers = this.labelsByLayer.get(DRAW_ID) ?? [];
    const positions = this._resolveDrawingLabelPositions(vertices);

    // Vertex add/remove or visibility changes are structural. They may create
    // or remove a marker once, but ordinary drag frames never reach this path.
    const needsStructuralRender = markers.length !== positions.length
      || positions.some((position, index) => Boolean(position) !== Boolean(markers[index]));
    if (needsStructuralRender) {
      markers.forEach((m) => {
        if (m) try { m.remove(); } catch {}
      });
      this.labelsByLayer.set(DRAW_ID, []);
      if (vertices.length >= 2) {
        this._renderLabels(DRAW_ID, vertices, true, false /* not closed ring during drawing */);
      }
      return;
    }

    // FLICKER-v283: retain each divIcon and change only its transform/text when
    // the collision-aware placement moves by a perceptible amount.
    positions.forEach((position, index) => {
      const marker = markers[index];
      if (!position || !marker) return;
      try {
        const current = this.map.latLngToContainerPoint(marker.getLatLng());
        if (shouldUpdateDrawLabelPosition(current, position.point)) {
          marker.setLatLng([position.latLng.lat, position.latLng.lng]);
        }
        const inner = marker.getElement?.()?.querySelector(".segment-label");
        if (inner && inner.textContent !== position.label) inner.textContent = position.label;
      } catch {
        // A map teardown may race a final drag frame; cleanup owns the marker.
      }
    });
  }

  private _resolveDrawingLabelPositions(vertices: LatLng[]): Array<{
    label: string;
    point: ScreenPoint;
    latLng: { lat: number; lng: number };
  } | null> {
    const count = Math.max(0, vertices.length - 1);
    if (count === 0) return [];

    try {
      const size = this.map.getSize?.();
      const viewport = { width: size?.x ?? 0, height: size?.y ?? 0 };
      if (viewport.width <= 0 || viewport.height <= 0) return Array(count).fill(null);

      const handlePoints: ScreenPoint[] = [];
      for (let index = 0; index < vertices.length; index += 1) {
        const vertex = vertices[index];
        const next = vertices[index + 1];
        handlePoints.push(this.map.latLngToContainerPoint(vertex));
        if (next) {
          handlePoints.push(this.map.latLngToContainerPoint({
            lat: (vertex.lat + next.lat) / 2,
            lng: (vertex.lng + next.lng) / 2,
          }));
        }
      }

      const placedLabelPoints: ScreenPoint[] = [];
      return Array.from({ length: count }, (_, index) => {
        const a = vertices[index];
        const b = vertices[index + 1];
        if (!a || !b || !isDisplayableSegmentDistance(a.distanceTo?.(b))) return null;

        const start = this.map.latLngToContainerPoint(a);
        const end = this.map.latLngToContainerPoint(b);
        const screenDistance = Math.hypot(start.x - end.x, start.y - end.y);
        if (screenDistance < MIN_SCREEN_DIST_PX) return null;

        const point = resolveDrawLabelPlacement({
          start,
          end,
          handlePoints,
          labelPoints: placedLabelPoints,
          viewport,
        });
        placedLabelPoints.push(point);
        const latLng = this.map.containerPointToLatLng(point);
        return { label: formatPerimeter(a.distanceTo(b)), point, latLng };
      });
    } catch {
      return Array(count).fill(null);
    }
  }

  /**
   * FAM-quality drag update: mutate only the 2 adjacent labels for draggedIdx.
   * Uses setLatLng + textContent mutation — NO DOM destroy, NO flicker.
   * R8: setIcon = DOM destroy during drag => use textContent instead.
   *
   * @param layer - The layer being dragged
   * @param draggedIdx - Index of the dragged vertex (from e.indexPath[last])
   */
  updateForLayerDrag(layer: any, draggedIdx: number): void {
    const id = this._layerId(layer);
    const labels = this.labelsByLayer.get(id) ?? [];
    const latLngs = extractOuterRing(layer);
    const n = latLngs.length;

    if (n < 2 || labels.length !== n) {
      // Fallback: full re-render (only fires before first render or after mismatch)
      this.updateForLayer(layer);
      return;
    }

    // Only update the 2 edges adjacent to the dragged vertex
    const affected = [(draggedIdx - 1 + n) % n, draggedIdx];
    for (const i of affected) {
      const marker = labels[i];
      if (!marker) continue; // null slot — skip (R12: null placeholder for skipped edges)
      const a = latLngs[i];
      const b = latLngs[(i + 1) % n];
      if (!a || !b) continue;
      const dist = typeof a.distanceTo === "function" ? a.distanceTo(b) : 0;
      const midLat = (a.lat + b.lat) / 2;
      const midLng = (a.lng + b.lng) / 2;
      const label = formatPerimeter(dist);
      try {
        // R8: Use setLatLng (CSS transform — safe, no DOM destroy)
        marker.setLatLng([midLat, midLng]);
        // Mutate textContent instead of setIcon to avoid DOM destroy during drag
        const el = marker.getElement?.();
        const inner = el?.querySelector(".segment-label");
        if (inner && inner.textContent !== label) inner.textContent = label;
      } catch {}
    }
  }

  /**
   * Append labels for a layer WITHOUT clearing existing labels first.
  * Use for multi-polygon re-render after pm:remove: clear once, then appendForLayer for each remaining polygon.
  */
  appendForLayer(layer: any): void {
    // A hidden polygon is removed from the Leaflet map but remains in the
    // modal's layer registry. Any delayed zoom, load, rotate, or drag rebuild
    // must therefore refuse to create labels for a detached layer.
    if (typeof this.map?.hasLayer === "function" && !this.map.hasLayer(layer)) return;
    const latLngs = extractOuterRing(layer);
    if (latLngs.length < 2) return;
    this._renderLabels(this._layerId(layer), latLngs, false, true);
  }

  destroy(): void {
    this.clearLabels();
  }

  /**
   * Internal: render distance labels for a LatLng array.
   * R12: MUST push null for EVERY skipped edge to maintain index invariant.
   *
   * @param layerId - Key for labelsByLayer Map
   * @param latLngs - Flat array of LatLng objects
   * @param isDrawing - Whether we are in drawing mode (blue labels)
   * @param closedRing - Whether to include the closing edge (last=>first vertex)
   */
  private _renderLabels(layerId: number, latLngs: LatLng[], isDrawing: boolean, closedRing: boolean): void {
    if (!this.labelsByLayer.has(layerId)) this.labelsByLayer.set(layerId, []);
    const labelArr = this.labelsByLayer.get(layerId)!;

    const count = closedRing ? latLngs.length : latLngs.length - 1;
    const placedDrawingLabelPoints: ScreenPoint[] = [];
    const drawingHandlePoints: ScreenPoint[] = [];
    let drawingViewport: { width: number; height: number } | null = null;

    // Drawing-mode midpoint handles occupy the exact segment midpoint. Build
    // their screen positions once so visible distance text can be displaced
    // without shrinking the 44px mobile touch target or changing geometry.
    if (isDrawing) {
      try {
        const size = this.map.getSize?.();
        drawingViewport = { width: size?.x ?? 0, height: size?.y ?? 0 };
        for (let index = 0; index < latLngs.length; index += 1) {
          const vertex = latLngs[index];
          const next = latLngs[(index + 1) % latLngs.length];
          drawingHandlePoints.push(this.map.latLngToContainerPoint(vertex));
          if (index < count && next) {
            drawingHandlePoints.push(this.map.latLngToContainerPoint({
              lat: (vertex.lat + next.lat) / 2,
              lng: (vertex.lng + next.lng) / 2,
            }));
          }
        }
      } catch {
        drawingViewport = null;
      }
    }

    for (let i = 0; i < count; i++) {
      const a = latLngs[i];
      const b = latLngs[(i + 1) % latLngs.length];

      // Validate that a and b are real LatLng objects
      if (
        typeof a?.lat !== "number" ||
        typeof a?.lng !== "number" ||
        typeof b?.lat !== "number" ||
        typeof b?.lng !== "number"
      ) {
        labelArr.push(null); // R12: null, not skip — maintain index invariant
        continue;
      }

      const dist = typeof a.distanceTo === "function" ? a.distanceTo(b) : 0;
      if (!isDisplayableSegmentDistance(dist)) {
        labelArr.push(null); // R12: null, not skip
        continue;
      }

      // BUG-4 FIX: Skip label if screen distance is too small (prevents overlap)
      try {
        const ptA = this.map.latLngToContainerPoint(a);
        const ptB = this.map.latLngToContainerPoint(b);
        const dx = ptA.x - ptB.x;
        const dy = ptA.y - ptB.y;
        const screenDist = Math.sqrt(dx * dx + dy * dy);
        if (screenDist < MIN_SCREEN_DIST_PX) {
          labelArr.push(null); // R12: null, not skip
          continue;
        }
      } catch {
        labelArr.push(null); // R12: null, not skip
        continue;
      }

      // Midpoint
      const midLat = (a.lat + b.lat) / 2;
      const midLng = (a.lng + b.lng) / 2;

      // Sprint v77 Bug C Fix: offset label 20px perpendicular OUTSIDE the polygon edge
      // (Claude Opus SaaS production-grade spec: labels should not overlay polygon fill)
      // Sprint v111 Fix 2: Skip offset during draw mode — polygon is open (closedRing=false)
      // so centroid is ill-defined and outsideSign flips edge-by-edge, placing labels
      // randomly inside/outside. During drawing, labels sit ON the midpoint (no offset).
      // Offset is only applied for completed/saved polygons where centroid is valid.
      let labelLatLng: [number, number] = [midLat, midLng];
      if (isDrawing && drawingViewport && drawingViewport.width > 0 && drawingViewport.height > 0) {
        try {
          const placement = resolveDrawLabelPlacement({
            start: this.map.latLngToContainerPoint(a),
            end: this.map.latLngToContainerPoint(b),
            handlePoints: drawingHandlePoints,
            labelPoints: placedDrawingLabelPoints,
            viewport: drawingViewport,
          });
          const ll = this.map.containerPointToLatLng(placement);
          labelLatLng = [ll.lat, ll.lng];
          placedDrawingLabelPoints.push(placement);
        } catch {
          // Keep the geographic midpoint as a safe rendering fallback.
        }
      } else if (!isDrawing) {
        try {
          const ptA = this.map.latLngToContainerPoint(a);
          const ptB = this.map.latLngToContainerPoint(b);
          const edgeDx = ptB.x - ptA.x;
          const edgeDy = ptB.y - ptA.y;
          const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
          if (edgeLen > 0) {
            // Perpendicular direction (rotate edge 90°)
            const perpX = -edgeDy / edgeLen;
            const perpY = edgeDx / edgeLen;
            // Determine outside direction using polygon centroid
            const centroid = this._getCentroid(latLngs);
            const centroidPt = this.map.latLngToContainerPoint(centroid);
            const midPt = this.map.latLngToContainerPoint({ lat: midLat, lng: midLng });
            const toCentX = centroidPt.x - midPt.x;
            const toCentY = centroidPt.y - midPt.y;
            // dot > 0 means perp points toward centroid → flip to point outside
            const dotProduct = perpX * toCentX + perpY * toCentY;
            const outsideSign = dotProduct > 0 ? -1 : 1;
            // Sprint v115.3 Bug3: OFFSET_PX=0 unconditionally.
            // v115 Bug3 set OFFSET_PX=0 only in draw mode (isDrawing), but the reported jitter
            // happens in EDIT mode (isDrawing=false) during vertex drag — centroid re-projects
            // every frame causing the label to jump 20px in random directions.
            // Setting OFFSET_PX=0 everywhere: labels sit on the midpoint (no perpendicular offset)
            // which is stable and avoids the jitter entirely.
            const OFFSET_PX = 0; // Sprint v115.3 Bug3: was `isDrawing ? 0 : 20` (no-op for edit mode)
            const labelPt = {
              x: midPt.x + perpX * OFFSET_PX * outsideSign,
              y: midPt.y + perpY * OFFSET_PX * outsideSign,
            };
            const ll = this.map.containerPointToLatLng(labelPt);
            labelLatLng = [ll.lat, ll.lng];
          }
        } catch { /* fallback to midpoint */ }
      }

      const label = formatPerimeter(dist);
      const cssClass = isDrawing
        ? "segment-label segment-label--drawing"
        : "segment-label";

      // S1 v46 FIX: Apply background via inline style so Geoman edit mode cannot override it.
      // CSS `!important` cannot override inline styles; Geoman may inject CSS rules during edit
      // that strip the class-based background. Inline style guarantees the label is always visible.
      // Sprint v100 B8: add touch-action:none so segment labels do not intercept touch scroll/pan
     const bgStyle = isDrawing
        ? "background:rgba(0,0,0,0.72);color:#fff;font-size:10px;font-weight:600;padding:2px 5px;border-radius:4px;white-space:nowrap;pointer-events:none;user-select:none;line-height:1.4;touch-action:none;"
        : "background:rgba(0,0,0,0.72);color:#fff;font-size:10px;font-weight:600;padding:2px 5px;border-radius:4px;white-space:nowrap;pointer-events:none;user-select:none;line-height:1.4;touch-action:none;";

      // V3 v48: Dynamic iconSize based on label length to prevent clipping.
      // Each char ~6px wide at 10px font + 10px padding. Min 60px, max 120px.
      const iconW = Math.max(60, Math.min(120, label.length * 6 + 14));
      const iconH = 20;
      const icon = this.L.divIcon({
        className: "segment-label-icon",
        html: `<div class="${cssClass}" style="${bgStyle}">${label}</div>`,
        iconSize: [iconW, iconH],
        iconAnchor: [iconW / 2, iconH / 2], // centered
      });

      const marker = this.L.marker(labelLatLng, {
        icon,
        interactive: false,
        keyboard: false,
        zIndexOffset: 500,
        // U1 v47: pmIgnore:true prevents Geoman from calling enableRotate()/enableEdit() etc.
        // on these markers. Without this, Geoman's handleLayerAdditionInGlobalRotateMode
        // (which has a bug: uses _isRelevantForRemoval instead of _isRelevantForRotate)
        // calls enableRotate() on L.Marker which has no getLatLngs() => crash.
        pmIgnore: true,
      });

      marker.addTo(this.map);
      labelArr.push(marker); // push real marker
    }
  }

  // Sprint v77 Bug C Fix: helper to compute polygon centroid for outside-edge direction
  private _getCentroid(latLngs: { lat: number; lng: number }[]): { lat: number; lng: number } {
    let lat = 0, lng = 0;
    const n = latLngs.length;
    if (n === 0) return { lat: 0, lng: 0 };
    for (const p of latLngs) { lat += p.lat; lng += p.lng; }
    return { lat: lat / n, lng: lng / n };
  }

  // Sprint v78 Bug 2 Fix: hint label — shows live distance from last vertex to cursor during drawing
  private _hintMarker: any = null;

  updateHintLabel(midLatLng: { lat: number; lng: number }, distM: number): void {
    try {
      // BUG-v276: A touch starts on the last vertex before the cursor moves,
      // which previously produced a visible transient "0 m" label. Keep the
      // input geometry intact but remove meaningless feedback until a real
      // provisional segment exists.
      if (
        !isDisplayableSegmentDistance(distM)
        || !Number.isFinite(midLatLng?.lat)
        || !Number.isFinite(midLatLng?.lng)
      ) {
        this.clearHintLabel();
        return;
      }
      const label = formatPerimeter(distM);
      const iconW = Math.max(60, Math.min(120, label.length * 6 + 14));
      const iconH = 20;
      const icon = this.L.divIcon({
        className: 'segment-label-icon',
        html: `<div class="segment-label segment-label--drawing" style="background:rgba(0,0,0,0.72);color:#fff;font-size:10px;font-weight:600;padding:2px 5px;border-radius:4px;white-space:nowrap;pointer-events:none;user-select:none;line-height:1.4;">${label}</div>`,
        iconSize: [iconW, iconH],
        iconAnchor: [iconW / 2, iconH / 2],
      });
      if (this._hintMarker) {
        this._hintMarker.setLatLng([midLatLng.lat, midLatLng.lng]);
        this._hintMarker.setIcon(icon);
      } else {
        this._hintMarker = this.L.marker([midLatLng.lat, midLatLng.lng], {
          icon,
          interactive: false,
          keyboard: false,
          zIndexOffset: 600,
          pmIgnore: true,
        });
        this._hintMarker.addTo(this.map);
      }
    } catch {}
  }

  clearHintLabel(): void {
    try {
      if (this._hintMarker) {
        this._hintMarker.remove();
        this._hintMarker = null;
      }
    } catch {}
  }
}
